import { useEffect, useRef, type RefObject } from 'react';

/**
 * IntersectionObserver-based load-more for folder pagination.
 *
 * Observes a sentinel element inside a scroll container. When the sentinel
 * enters the viewport (with 200px bottom margin for early trigger), fires
 * `onLoadMore`. Only activates when `enabled` is true and `isLoading` is false.
 *
 * Returns a ref to attach to a sentinel `<div className="h-px" />`.
 */
export function useIntersectionLoadMore({
  onLoadMore,
  enabled,
  isLoading,
  getScrollRoot,
}: {
  onLoadMore: () => void;
  enabled: boolean;
  isLoading: boolean;
  getScrollRoot: () => HTMLElement | null;
}): RefObject<HTMLDivElement | null> {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (!enabled || isLoading) return;
    const el = sentinelRef.current;
    const root = getScrollRoot();
    if (!el || !root) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMoreRef.current();
      },
      { root, rootMargin: '0px 0px 200px 0px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, isLoading, getScrollRoot]);

  return sentinelRef;
}
